import { Component, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { Chats } from './modules/chats/chats';
import { MicaChatHeader } from './layout/mica-chat-header/mica-chat-header';
import { MicaChatConfiguracion } from './views/mica-chat-configuracion';
import { MicaChatFlujos } from './views/mica-chat-flujos';
import { MicaChatCampanas } from './views/mica-chat-campanas';
import { AppService } from '@shared/services/app.service';
import { MicaChatApiService } from '@core/services/api/mica-chat-api.service';
import { MicaChatService } from '@shared/services/mica-chat.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { IMicaChatTelefonoData } from '@core/interfaces/entidades/telefono.entidad';
import { ToastService } from '@shared/services/toast.service';

type TVista = 'chats' | 'flujos' | 'campanas' | 'configuracion';

@Component({
  selector: 'app-mica-chat',
  imports: [Chats, MicaChatHeader, MicaChatConfiguracion, MicaChatFlujos, MicaChatCampanas],
  templateUrl: './mica-chat.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mica-chat.css',
})
export class MicaChat implements OnInit {
  private readonly appService = inject(AppService);
  private readonly micaChatApi = inject(MicaChatApiService);
  private readonly micaChatService = inject(MicaChatService);
  private readonly sucursalService = inject(SucursalService);
  private readonly toastService = inject(ToastService);

  telefonos = signal<IMicaChatTelefonoData[]>([]);
  tieneTelefonos = signal<boolean | null>(null);
  vista = signal<TVista>('chats');

  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.micaChatApi.cargarTelefonos().subscribe({
          next: (telefonos) => {
            this.telefonos.set(telefonos);
            this.tieneTelefonos.set(telefonos.length > 0);
            this.micaChatService.setTelefonoSeleccionado(telefonos[0] || null);
          },
          error: () => this.tieneTelefonos.set(false),
        });
      }
    });
  }

  onTelefonoCambiado(telefono: IMicaChatTelefonoData | null) {
    this.micaChatService.setTelefonoSeleccionado(telefono);
  }

  recargarTelefonos() {
    this.micaChatApi.cargarTelefonos().subscribe({
      next: (telefonos) => {
        this.telefonos.set(telefonos);
        this.tieneTelefonos.set(telefonos.length > 0);
      },
    });
  }

  toggleComprobantes(telefono: IMicaChatTelefonoData) {
    const activar = !telefono.usar_para_comprobantes;
    this.micaChatApi.toggleComprobantes(telefono.id, activar).subscribe({
      next: () => {
        this.toastService.show(
          'top-right',
          'success',
          'Configurado',
          activar
            ? `"${telefono.titulo}" ahora envía comprobantes`
            : `"${telefono.titulo}" ya no envía comprobantes`,
        );
        this.recargarTelefonos();
      },
      error: (err: any) =>
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'Error al configurar',
        ),
    });
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'MicaChat',
      subtitle: 'Comunícate con tus clientes de forma rápida y sencilla',
      showBackButton: true,
      icon: 'bi bi-chat-dots',
    });
  }
}
